import { memo, useCallback, useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";

import LoopIcon from "@mui/icons-material/Loop";
import { Box, IconButton, Modal } from "@mui/material";

import Close from "@zenith/assets/icons/Close";
import Refresh from "@zenith/assets/icons/Refresh";
import { SpinnerComponent } from "@zenith/components";
import { ImageRequestDTO } from "@zenith/services/user";
import { removeBase64Prefix } from "@zenith/utils/Formaters";
import { CAMERA_MESSAGES } from "./constants";
import FaceArea from "./face-area";

/**
 * Props for Camera Component.
 * @interface
 * @name CameraComponentProps
 * @kind interface
 * @exports
 */
export interface CameraComponentProps {
  /**
   * If `true`, opens the Camera Component modal.
   * @property
   * @name open
   * @kind property
   * @memberof CameraComponentProps
   * @type {boolean}
   */
  open: boolean;
  /**
   * Sets if the Camera Component modal is open.
   * @property
   * @name setOpen
   * @kind property
   * @memberof CameraComponentProps
   * @type {React.Dispatch<React.SetStateAction<boolean>>}
   */
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * (optional)
   * Function that has the photos.
   * @method
   * @name getPhotos
   * @kind property
   * @memberof CameraComponentProps
   */
  getPhotos?: (photos: ImageRequestDTO[]) => void;
  /**
   * (optional)
   * If `true`, detects the face inside the Face Area.
   * @property
   * @name detectFace
   * @kind property
   * @memberof CameraComponentProps
   * @type {boolean | undefined}
   */
  detectFace?: boolean;
}

/**
 * The video constraints.
 * @constant
 * @name videoConstraints
 * @kind variable
 * @type {boolean | MediaTrackConstraints | undefined}
 */
const videoConstraints: MediaStreamConstraints["video"] = {
  width: 1280,
  height: 720,
  facingMode: "user",
};

/**
 * @deprecated For testing only.
 * @async
 * @function
 * @name consoleLogAsync
 * @kind variable
 * @param {string} message The `string` message.
 * @param {any=} other Anything.
 * @returns {Promise<void>}
 */
const consoleLogAsync = async (message: string, other?: any): Promise<void> => {
  await new Promise((resolve) => setTimeout(resolve, 0)).then(() =>
    console.info(message, other)
  );
};

/**
 * Memoized Camera Component.
 * @constant
 * @name CameraComponent
 * @kind variable
 * @type {React.NamedExoticComponent<CameraComponentProps>}
 */
const CameraComponent: React.NamedExoticComponent<CameraComponentProps> = memo(
  /**
   * Camera Component.
   * @function
   * @name CameraComponent
   * @kind function
   * @memberof CameraComponent
   * @param {CameraComponentProps} props
   * @returns {JSX.Element}
   */
  function CameraComponent(props: CameraComponentProps): JSX.Element {
    const webcamRef = useRef<Webcam>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [photos, setPhotos] = useState<ImageRequestDTO[]>([]);

    const [isLoading, setIsLoading] = useState(true);
    const [isTakingPhotos, setIsTakingPhotos] = useState(false);
    const [isPhotosTaken, setIsPhotosTaken] = useState(false);
    const [isFaceInsideArea, setIsFaceInsideArea] = useState(false);
    const [isFaceApiReady, setIsFaceApiReady] = useState(false);
    const [cameraError, setCameraError] = useState<string | null>(null);
    const timeoutIdsRef = useRef<NodeJS.Timeout[]>([]);

    // Add a timeout to force show the camera after 5 seconds even if face detection isn't ready
    useEffect(() => {
      const forceShowTimeout = setTimeout(() => {
        if (isLoading && webcamRef.current && webcamRef.current.video) {
          setIsLoading(false);
        }
      }, 5000);

      return () => {
        clearTimeout(forceShowTimeout);
      };
    }, [isLoading, props.detectFace]);

    const capture = useCallback(
      async (e: React.MouseEvent<HTMLElement>) => {
        // Prevent the default behavior of the event
        e.preventDefault();

        // Initialize an array to store the captured images to send to the BE
        const newArr: ImageRequestDTO[] = [];
        const totalNumberOfPhotos = 8;

        // Asynchronously take selfies in a delayed loop
        await (function takeSelfies(counter) {
          setIsTakingPhotos(true);
          // Use setTimeout to introduce a delay
          const timeoutId = setTimeout(async function () {
            // Capture an image using the webcam
            const image = webcamRef.current?.getScreenshot();

            if (!image) return;

            newArr.push({
              imageType:
                counter === totalNumberOfPhotos
                  ? ImageRequestDTO.imageType.SELFIE_BASE64
                  : ImageRequestDTO.imageType.LIVENESS_BASE64,
              imageContent: removeBase64Prefix(image),
              mimeExt: "jpeg", // hardcoded value since the camera only takes pictures in jpeg
            });

            // Set the state with the updated array of images
            setPhotos(newArr);

            // If there are more iterations remaining, recursively call the function
            if (--counter) {
              setIsPhotosTaken(false);
              takeSelfies(counter);
            } else {
              setIsPhotosTaken(true);
              setIsTakingPhotos(false);
            }
          }, 350);
          timeoutIdsRef.current.push(timeoutId);
        })(totalNumberOfPhotos);
        // await consoleLogAsync("Open me only when counter reaches 1", newArr);
      },
      [webcamRef]
    );

    /**
     * @method retake
     * @description Restarts the whole flow
     */
    const retake = () => {
      setIsTakingPhotos(false);
      setIsPhotosTaken(false);
      setPhotos([]);
      setIsLoading(true);
    };

    /**
     * @method handleModalExit
     * @description Exists the modal and resets the variables
     */
    const handleModalExit = () => {
      props.setOpen(!props.open);
      setIsLoading(true);
      setIsTakingPhotos(false);
      setIsPhotosTaken(false);
      setIsFaceInsideArea(false);
      setIsFaceApiReady(false);
      setPhotos([]);
    };

    /**
     * @method handleOnCameraReady
     * @description When camera is ready sets the loading of the
     *              camera to false in order to show the ui
     */
    const handleOnCameraReady = () => {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    };

    /**
     * Handle camera errors
     */
    const handleCameraError = (error: string | DOMException) => {
      console.error("Camera error:", error);
      let errorMessage = "Could not access camera";

      if (error instanceof DOMException) {
        if (error.name === "NotAllowedError") {
          errorMessage =
            "Camera access denied. Please allow camera permissions in your browser.";
        } else if (error.name === "NotFoundError") {
          errorMessage =
            "No camera found. Please ensure your camera is connected.";
        } else if (error.name === "NotReadableError") {
          errorMessage = "Camera is in use by another application.";
        }
      }

      setCameraError(errorMessage);
      setIsLoading(false);
    };

    /**
     * @method sendPhotosToParent
     * @description Sends the photos array to the parent component
     */
    const sendPhotosToParent = () => {
      if (props.getPhotos) props.getPhotos(photos);
      handleModalExit();
    };

    /**
     * @method getFaceDetectionValues
     * @description If face detection is enabled it checks if the face is inside the face area
     *              and sets the isFaceInsideArea to true or false
     * @param isFaceInsideArea
     */
    const getFaceDetectionValues = useCallback(
      (isFaceInsideAreaParam: boolean, isFaceApiReadyParam: boolean) => {
        setIsFaceInsideArea(() => {
          return isFaceInsideAreaParam;
        });
        setIsFaceApiReady(() => {
          return isFaceApiReadyParam;
        });
      },
      []
    );

    /**
     * If the component is taking photos and the face leaves the
     * face area it stops the takeSelfies method
     */
    useEffect(() => {
      if (isTakingPhotos && !isFaceInsideArea) {
        setIsTakingPhotos(false);

        timeoutIdsRef.current.forEach((timeoutId) => {
          clearTimeout(timeoutId);
        });
        // Clear the stored timeout IDs
        timeoutIdsRef.current = [];
      }
    }, [isTakingPhotos, isFaceInsideArea]);

    const message =
      isTakingPhotos && !isPhotosTaken
        ? CAMERA_MESSAGES.TAKING_PHOTOS
        : !isTakingPhotos && isPhotosTaken
        ? CAMERA_MESSAGES.PHOTOS_TAKEN
        : !isTakingPhotos && !isPhotosTaken && isFaceInsideArea
        ? CAMERA_MESSAGES.FACE_INSIDE_AREA
        : CAMERA_MESSAGES.FACE_OUTSIDE_AREA;

    return (
      <Modal
        open={props.open}
        onClose={handleModalExit}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          className={`absolute top-1/2 left-1/2 flex h-auto -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg ${
            isLoading
              ? "bg-transparent"
              : " h-60 w-11/12 bg-white md:h-auto md:w-camera-modal"
          }`}
        >
          {isLoading && (
            <div className="flex h-full justify-center p-5">
              <SpinnerComponent type="primary" showIcon={true} />
            </div>
          )}

          {cameraError && (
            <div className="p-5 text-center">
              <h1 className="mb-2 font-bold">Camera Error</h1>
              <p className="mb-4 text-red-600">{cameraError}</p>
              <button className="secondary-button" onClick={handleModalExit}>
                Close
              </button>
            </div>
          )}

          {!isLoading && !cameraError && (
            <div className="p-5">
              <div className="flex justify-between">
                <h1 className="font-bold">
                  {!isPhotosTaken ? "Take Photo" : "Review Photo"}
                </h1>
                <IconButton
                  size="small"
                  className="self-end"
                  onClick={handleModalExit}
                >
                  <Close height={16} width={16} />
                </IconButton>
              </div>

              <span className="mb-14 text-xs">{message}</span>
            </div>
          )}

          <div className="relative flex h-full w-full justify-center px-7">
            {!isLoading && !cameraError && isPhotosTaken ? (
              <div className="flex h-selfie w-selfie justify-center overflow-hidden">
                <img
                  src={"data:image/jpeg;base64," + photos[0].imageContent}
                  alt="selfie"
                  className="h-auto w-full object-cover"
                />
              </div>
            ) : (
              !cameraError && (
                <>
                  <Webcam
                    className={`rounded-lg ${
                      isLoading ? "hidden" : "h-[200px] md:h-full"
                    }`}
                    audio={false}
                    width={846}
                    height={450}
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    videoConstraints={videoConstraints}
                    onUserMedia={handleOnCameraReady}
                    onLoadedData={handleOnCameraReady}
                    onUserMediaError={handleCameraError}
                  />
                  <canvas ref={canvasRef} style={{ position: "absolute" }} />
                  {!isLoading && (
                    <div
                      className={`absolute inset-0 flex items-center justify-center px-7
                ${props.detectFace && !isFaceApiReady ? "hidden" : ""}`}
                    >
                      <FaceArea
                        webcamRef={webcamRef}
                        startAnimation={isTakingPhotos}
                        animationTime={3}
                        canvasRef={canvasRef}
                        detectFace={props.detectFace}
                        getFaceDetectionValues={getFaceDetectionValues}
                      />
                    </div>
                  )}
                  {props.detectFace && !isFaceApiReady && !isLoading && (
                    <div className="absolute inset-0 left-1/2 flex w-full -translate-x-1/2 items-center justify-center rounded-lg bg-black md:w-camera-width">
                      <LoopIcon className="animate-spin-reverse text-white" />
                    </div>
                  )}
                </>
              )
            )}
          </div>
          <div
            className={`${
              isLoading ? "" : "mt-12 mb-5"
            } flex justify-center px-5`}
          >
            {!isLoading && !cameraError && !isPhotosTaken ? (
              <button
                className="primary-button"
                onClick={capture}
                disabled={
                  isTakingPhotos || (props.detectFace && !isFaceInsideArea)
                }
              >
                Take Photo
              </button>
            ) : !isLoading && !cameraError && isPhotosTaken ? (
              <div className="flex gap-x-5">
                <button
                  className="secondary-button"
                  onClick={retake}
                  disabled={isLoading}
                >
                  <Refresh className="mr-1" />
                  Re-take Photo
                </button>
                <button
                  className="primary-button"
                  onClick={sendPhotosToParent}
                  disabled={isLoading}
                >
                  Yes, use this photo
                </button>
              </div>
            ) : null}
          </div>
        </Box>
      </Modal>
    );
  }
);

export default CameraComponent;
